import { Avatar, Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { marketplace } from '@/api/endpoints';

export function PublicClinicianPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const q = useApiQuery({
    queryKey: ['marketplace', 'clinician', slug],
    queryFn:  () => marketplace.clinicians.get(slug!),
    enabled:  !!slug,
  });

  return (
    <>
      <PageHeader title="Clinician profile" />
      <DataState query={q}>
        {(profile: any) => (
          <Stack spacing={3} sx={{ maxWidth: 680, mx: 'auto', mt: 3, px: 2 }}>

            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={profile.photo_url || undefined}
                          sx={{ width: 80, height: 80, fontSize: 32 }}>
                    {profile.display_name?.[0]}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5">{profile.display_name}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                      {profile.specialties?.map((s: string) => (
                        <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                      ))}
                    </Stack>
                    {profile.languages?.length > 0 && (
                      <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        Languages: {profile.languages.join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Button variant="contained" size="large"
                          onClick={() => navigate('/book?practice=' + slug)}>
                    Book appointment
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {profile.bio && (
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                    About
                  </Typography>
                  <Typography variant="body1">{profile.bio}</Typography>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent>
                <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                  Consultation modes
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {profile.offers_in_person && <Chip label="In person" color="success" size="small" />}
                  {profile.offers_video     && <Chip label="Video"     color="info"    size="small" />}
                  {profile.offers_phone     && <Chip label="Phone"     color="info"    size="small" />}
                  {profile.offers_async     && <Chip label="Async chat" color="info"  size="small" />}
                  {!profile.offers_in_person && !profile.offers_video &&
                   !profile.offers_phone    && !profile.offers_async  && (
                    <Typography variant="body2" color="text.secondary">
                      No modalities listed yet.
                    </Typography>
                  )}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color={profile.accepts_new_patients ? 'success.main' : 'text.disabled'}>
                  {profile.accepts_new_patients ? '✓ Accepting new patients' : '✗ Not accepting new patients'}
                </Typography>
              </CardContent>
            </Card>

          </Stack>
        )}
      </DataState>
    </>
  );
}
